import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {add, deleteRecord, fetch, update} from '../services/PricesService';
import {Price} from '../types';
import {notifyError, notifySuccess} from '../../../components/ServiceNotifications';

export const usePrice = (priceId?: string) => {
    const queryClient = useQueryClient();

    const priceQuery = useQuery({
        queryKey: ['price', priceId],
        queryFn: () => fetch(priceId!),
        enabled: !!priceId,
    });

    const createMutation = useMutation({
        mutationFn: (values: Price) => {
            const payload = {...values} as Record<string, unknown>;
            delete payload.created;
            delete payload.updated;
            const modified = Object.assign({}, payload, {unitAmount: parseInt((payload as Price).unitAmount) * 100});
            return add(modified);
        },
        onSuccess: () => {
            notifySuccess('Price created');
            queryClient.invalidateQueries({queryKey: ['prices']});
        },
        onError: notifyError,
    });

    const updateMutation = useMutation({
        mutationFn: (values: Price) => {
            if (!priceId) throw new Error('priceId is required parameter');
            const payload = {...values} as Record<string, unknown>;
            delete payload.created;
            delete payload.updated;
            const modified = Object.assign({}, payload, {unitAmount: parseInt((payload as unknown as Price).unitAmount) * 100});
            return update(priceId, modified as unknown as Price);
        },
        onSuccess: () => {
            notifySuccess('Price updated');
            queryClient.invalidateQueries({queryKey: ['prices']});
            queryClient.invalidateQueries({queryKey: ['price', priceId]});
        },
        onError: notifyError,
    });

    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!priceId) throw new Error('priceId is required parameter');
            return deleteRecord(priceId);
        },
        onSuccess: () => {
            notifySuccess('Price deleted');
            queryClient.invalidateQueries({queryKey: ['prices']});
        },
        onError: notifyError,
    });

    return {
        record: priceQuery.data,
        loading: priceQuery.isLoading,
        fetchRecord: priceQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation,
    };
};
